/**
 * Mini Cart Web Component
 * Vanilla JS implementation following existing codebase patterns
 */
import formatMoney from '../helpers/format-money.js';
import { getCroppedImageUrl } from '../helpers/get-cropped-image-url.js';

class MiniCart extends HTMLElement {
  constructor() {
    super();

    // State
    this.data = null;
    this.cartIsOpen = false;
    this.cartIsBlocked = true;
    this.loading = true;
    this.initialLoading = true;
    this.isFetching = false;
    this.isUpdating = false;
    this.lastFetchTime = 0;
    this.upsellProducts = [];
    this.displayedUpsellProducts = [];
    this.selectedVariantIds = {};
    this.quantityRuleMap = {};
    this.loadingProducts = new Set();
    this.recentlyViewedProducts = [];
    this.fallbackProducts = [];
    this.initialDisplayCount = 5;
    this.additionalDisplayCount = 5;

    // Accordion state
    this.activeAccordion = null;
    this.oneAccordionMode = window.miniCartCustomSettings?.oneAccordion || false;

    // Free samples state
    this.freeSamplesConfig = window.freeSamples || { active: false, tiers: [] };
    this.addedSamples = [];
    this.recentlyAddedSample = null;

    // GWP state
    this.gwpConfig = window.gwpConfig || { active: false, products: [] };    
    this.addedGWP = [];
    this.addedGWPCount = 0;
    this.recentlyAddedGWP = null;

    // Discount code state
    this.appliedCodes = [];
    this.discountCodeInput = '';
    this.isApplyingDiscount = false;
    this.discountErrorMessage = '';

    // Order note state
    this.orderNote = '';
    this.isOrderNoteUpdating = false;
    this.hasUnsavedChanges = false;

    // Shipping rates state
    this.shippingRates = [];
    this.formCountry = '';
    this.formProvince = '';
    this.formZip = '';
    this.availableStates = [];

    // Settings
    this.settings = window.miniCartCustomSettings || {};
    this.tableLayout = this.getAttribute('data-table-layout') === 'true';

    // Bind methods
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    this.handleCartUpdated = this.handleCartUpdated.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    document.addEventListener('keydown', this.handleEscapeKey);
    document.addEventListener('cart:updated', this.handleCartUpdated);

    // Expose global functions
    window.closeMiniCart = () => this.closeMiniCart();
    window.toggleMiniCart = (direction) => this.toggleMiniCart(direction);
    window.openMiniCart = () => this.openMiniCart();
    window.getCart = () => this.getCart();
    window.emptyMiniCart = () => this.emptyMiniCart();

    // Initial render and fetch
    this.render();
    this.getCart();
    this.checkCart();
    this.fetchRecentlyViewedProducts();
    this.fetchCustomProducts();
    this.loadDiscountCodesFromStorage();

    this.loading = false;
    this.cartIsBlocked = false;
  }

  cleanup() {
    document.removeEventListener('keydown', this.handleEscapeKey);
    document.removeEventListener('cart:updated', this.handleCartUpdated);
  }

  // Utility functions
  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  getShopifyImageUrl(imageUrl, width) {
    if (!imageUrl) return '';
    if (imageUrl.includes('?')) {
      return `${imageUrl}&width=${width}`;
    }
    return `${imageUrl}?width=${width}`;
  }

  // Cart operations
  async getCart() {
    if (this.isFetching) return;

    this.isFetching = true;
    this.cartIsBlocked = true;

    try {
      const response = await fetch(Shopify.routes.root + 'cart.js');
      const data = await response.json();
        if (data.items && data.items.length > 0) {
      await Promise.all(data.items.map(async (item) => {
        try {
          const productRes = await fetch(`/products/${item.handle}.js`);
          const product = await productRes.json();
          const variant = product.variants.find(v => v.id === item.variant_id);
          
          if (variant && variant.compare_at_price) {
            item.compare_at_price = variant.compare_at_price;
          }
        } catch (err) {
          console.warn('Failed to fetch compare price for', item.product_title);
        }
      }));
    }

      const footerEl = document.querySelector('.js-mini-cart-footer');
      if (footerEl) {
        if (data.items && data.items.length > 0) {
          footerEl.classList.remove('!hidden');
        } else {
          footerEl.classList.add('!hidden');
        }
      }

      // Always update cart data
      this.data = data;
      window.cartTotal = data.total_price || 0;
      window.cartCount = data.item_count || 0;
      this.updateSamplesStateFromCart(data);
      this.updateGWPStateFromCart(data);

      if (data.items && data.items.length > 0) {
        this.updateCartValues(data);
        await this.fetchQuantityRulesForCart(data.items);
        await this.initUpsellProducts();
      }

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));

    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      this.isFetching = false;
      this.isUpdating = false;
      this.cartIsBlocked = false;
      this.initialLoading = false;
      this.lastFetchTime = Date.now();
      this.updateCartCounters();
      this.render();
    }
  }

  updateCartValues(response) {
    this.cartIsBlocked = false;
    this.loading = false;
    this.data = response;
    this.updateSamplesStateFromCart(response);
    this.updateGWPStateFromCart(response);

    window.cartTotal = response.total_price || 0;
    window.cartCount = response.item_count || 0;

    this.updateCartCounters();

    if (response.total_price === 0 && response.item_count === 0) {
      this.emptyMiniCart();
    }

    if (response.items && response.items.length > 0) {
      setTimeout(() => {
        this.refetchComparePrices(response.items);
      }, 0);
    } else {
      this.render();
    }  
  }

  updateCartCounters() {
    const cartTotalCounters = document.querySelectorAll('.js-cart-total');
    cartTotalCounters.forEach((element) => {
      const itemCount = this.data?.item_count || 0;
      if (itemCount === 0) {
        element.style.display = 'none';
      } else {
        element.style.display = 'flex';
        element.innerHTML = `<span class="cart-total__count">${itemCount}</span>`;
      }
    });
  }

  async updateItemQuantity(itemKey, quantity, itemId) {
    const itemIdStr = String(itemId);
    if (this.loadingProducts.has(itemIdStr)) return;

    this.loadingProducts.add(itemIdStr);
    this.isUpdating = true;
    this.render();

    try {
      const response = await fetch(Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: quantity }),
      });

      if (!response.ok) throw new Error('Failed to update cart');

      const data = await response.json();
      this.updateCartValues(data);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));

      if (quantity === 0) {
        await this.initUpsellProducts();
      }

    } catch (error) {
      console.error('Error updating cart:', error);
    } finally {
      this.loadingProducts.delete(itemIdStr);
      this.isUpdating = false;
      this.render();
    }
  }

  async updateSellingPlan(item, sellingPlanId) {
    const itemIdStr = String(item.id);
    if (this.loadingProducts.has(itemIdStr)) return;

    this.loadingProducts.add(itemIdStr);
    this.isUpdating = true;
    this.render();

    try {
      const response = await fetch(Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: item.key,
          quantity: item.quantity,
          selling_plan: sellingPlanId || null,
        }),
      });

      if (!response.ok) throw new Error('Failed to update selling plan');

      const data = await response.json();
      this.updateCartValues(data);

    } catch (error) {
      console.error('Error updating selling plan:', error);
      alert(error.message);
    } finally {
      this.loadingProducts.delete(itemIdStr);
      this.isUpdating = false;
      this.render();
    }
  }

  async emptyMiniCart() {
    this.cartIsBlocked = true;
    this.render();

    try {
      const response = await fetch(Shopify.routes.root + 'cart/clear.js', { method: 'POST' });
      const data = await response.json();
      this.data = data;
      await this.getCart();
      this.updateCartCounters();
      document.dispatchEvent(new CustomEvent('miniCart:emptied'));
    } catch (error) {
      console.error('Error emptying cart:', error);
    } finally {
      this.cartIsBlocked = false;
      this.render();
    }
  }

  // Mini cart visibility
  toggleMiniCart(direction) {
    if (direction === 'close') {
      this.cartIsOpen = false;
      document.body.classList.remove('mini-cart-open');
    }
    if (direction === 'open') {
      this.cartIsOpen = true;
      document.body.classList.remove('nav-open');
      document.body.classList.add('mini-cart-open');
      const isScrolled = document.body.classList.contains('scrolled');
      const miniCartWrap = document.querySelector('.mini-cart-wrap');
      if (miniCartWrap) {
        miniCartWrap.style.top = isScrolled ? '0px' : '30px';
      }
    }
    this.render();
  }

  openMiniCart() {
    this.cartIsOpen = true;
    document.body.classList.remove('nav-open');
    document.body.classList.add('mini-cart-open');
    this.render();
  }

  closeMiniCart() {
    this.cartIsOpen = false;
    document.body.classList.remove('mini-cart-open');
    this.render();
  }

  checkCart() {
    if (document.querySelector('.page-cart')) {
      this.cartIsOpen = true;
    }
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape' && this.cartIsOpen) {
      this.closeMiniCart();
    }
  }

  handleCartUpdated(event) {
    if (this.isFetching || this.isUpdating) return;

    // If we have fresh cart data in the event, use it and skip fetching
    if (event.detail) {
      this.data = event.detail;
      this.updateSamplesStateFromCart(event.detail);
      this.updateGWPStateFromCart(event.detail);
      this.updateCartCounters();
      this.render();
      return;
    }

    // Debounce: skip if we fetched recently (within 500ms)
    const now = Date.now();
    if (now - this.lastFetchTime < 500) return;

    this.getCart();
  }

  // Quantity rules
  async fetchInventoryForHandle(handle) {
    const res = await fetch(
      `/products/${handle}?section_id=variant-inventory&_=${Date.now()}`,
      { cache: 'no-store' }
    );
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector('#variant-inventory-data');
    if (!node) throw new Error('variant-inventory section missing');
    return JSON.parse(node.textContent);
  }

  applyVariantRule(item, variant) {
    const ruleMax = variant?.quantity_rule?.max ?? null;
    const invManaged = variant?.inventory_management != null && variant?.inventory_management !== '';
    const invPolicy = variant?.inventory_policy;
    const invQty = variant?.inventory_quantity ?? null;
    const tracked = invManaged && invPolicy === 'deny';

    let effectiveMax = ruleMax;
    if (tracked && invQty !== null) {
      effectiveMax = ruleMax !== null ? Math.min(ruleMax, invQty) : invQty;
    }

    this.quantityRuleMap[item.id] = {
      min: variant?.quantity_rule?.min ?? 1,
      max: effectiveMax,
      increment: variant?.quantity_rule?.increment ?? 1,
      inventory: invQty,
      tracked: tracked,
    };
  }

  async fetchQuantityRulesForCart(items) {
    const byHandle = new Map();
    for (const item of items) {
      if (!byHandle.has(item.handle)) byHandle.set(item.handle, []);
      byHandle.get(item.handle).push(item);
    }

    await Promise.all([...byHandle.entries()].map(async ([handle, group]) => {
      try {
        const payload = await this.fetchInventoryForHandle(handle);
        const variants = payload.variants || [];
        for (const item of group) {
          const variant = variants.find(v => v.id === item.variant_id);
          this.applyVariantRule(item, variant);
        }
      } catch (err) {
        console.warn('Failed to fetch quantity rules for handle', handle, err);
        for (const item of group) {
          if (!this.quantityRuleMap[item.id]) {
            this.quantityRuleMap[item.id] = { min: 1, max: null, increment: 1, inventory: null, tracked: false };
          }
        }
      }
    }));
  }

  // Upsell products
  async initUpsellProducts() {
    if (!this.data?.items?.length) return;

    const productIdsInCart = this.data.items.map(item => item.product_id);
    const allRecommendedProducts = [];

    for (let i = 0; i < productIdsInCart.length; i++) {
      const productId = productIdsInCart[i];

      try {
        const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=${this.settings.numberOfRecommendedProducts || 5}&intent=related`);

        if (!response.ok) continue;

        const newProductData = await response.json();
        if (newProductData?.products?.length) {
          allRecommendedProducts.push(...newProductData.products);
        }

        if (i < productIdsInCart.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 150));
        }
      } catch (error) {
        console.error(`Error fetching recommendations for product ${productId}:`, error);
      }
    }

    if (!allRecommendedProducts.length) {
      this.upsellProducts = [];
      this.displayedUpsellProducts = [];
      return;
    }

    // Remove duplicates
    const uniqueRecommendedProducts = allRecommendedProducts.filter((product, index, self) =>
      index === self.findIndex((p) => p.id === product.id)
    );

    // Filter out products already in cart
    const filteredRecommendedProducts = uniqueRecommendedProducts.filter(product =>
      !productIdsInCart.includes(product.id)
    );

    this.upsellProducts = filteredRecommendedProducts;

    // Set up variant selection
    this.upsellProducts.forEach(product => {
      if (product.variants?.length > 0) {
        const availableVariant = product.variants.find(variant => variant.available);
        if (availableVariant) {
          this.selectedVariantIds[product.id] = availableVariant.id;
        }
      }
    });

    this.updateDisplayedProducts();
  }

  updateDisplayedProducts() {
    this.displayedUpsellProducts = this.upsellProducts.slice(0, this.initialDisplayCount);
    this.render();
  }

  showMoreUpsell() {
    this.initialDisplayCount += this.additionalDisplayCount;
    this.updateDisplayedProducts();
  }

  async addUpsellProductToCart(event, product) {
    event.preventDefault();
    if (this.isAnyLoading()) return;

    const productIdStr = String(product.id);
    this.loadingProducts.add(productIdStr);
    this.render();

    const formData = new FormData(event.target);

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to add to cart: ${errorText}`);
      }

      const minicartMergeSection = this.querySelector('.mini-cart__merge-section');
      if (minicartMergeSection) {
        minicartMergeSection.scrollTo({ top: 0, behavior: 'smooth' });
      }

      if (document.querySelector('.page-cart')) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      await this.getCart();

    } catch (error) {
      console.error('Error adding upsell product to cart:', error);
    } finally {
      this.loadingProducts.delete(productIdStr);
      this.cartIsBlocked = false;
      this.render();
    }
  }

  // Recently viewed products
  async fetchRecentlyViewedProducts() {
    const viewedProducts = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
    if (!viewedProducts.length) {
      this.recentlyViewedProducts = [];
      return;
    }

    const fetched = await Promise.all(
      viewedProducts.map(async (productId) => {
        try {
          const res = await fetch(`/search/suggest.json?q=id:${productId}&resources[type]=product`);
          const data = await res.json();
          const found = data.resources.results.products[0];
          if (!found) return null;

          const full = await fetch(`/products/${found.handle}.js`);
          const product = await full.json();

          return {
            id: product.id,
            title: product.title,
            handle: product.handle,
            featured_image: product.featured_image,
            price: product.price_max / 100,
            compare_at_price: product.compare_at_price_max ? product.compare_at_price_max / 100 : null,
            variants: product.variants,
            vendor: product.vendor,
          };
        } catch (err) {
          console.error('Failed to fetch product', err);
          return null;
        }
      })
    );

    this.recentlyViewedProducts = fetched.filter(Boolean);
    this.render();
  }

  async fetchCustomProducts() {
    const customProductList = this.settings.customProductList || [];
    if (!customProductList.length) {
      this.fallbackProducts = [];
      return;
    }

    const fetched = await Promise.all(
      customProductList.map(async (productHandle) => {
        try {
          const full = await fetch(`/products/${productHandle}.js`);
          const product = await full.json();

          return {
            id: product.id,
            title: product.title,
            handle: product.handle,
            featured_image: product.featured_image,
            price: product.price_max / 100,
            compare_at_price: product.compare_at_price_max ? product.compare_at_price_max / 100 : null,
            variants: product.variants,
            vendor: product.vendor,
          };
        } catch (err) {
          console.error('Failed to fetch product', err);
          return null;
        }
      })
    );

    this.fallbackProducts = fetched.filter(Boolean);
    this.render();
  }

  getProductsToDisplay() {
    return this.recentlyViewedProducts.length > 0
      ? this.recentlyViewedProducts
      : this.fallbackProducts;
  }

  getProductsHeading() {
    return this.recentlyViewedProducts.length > 0
      ? (this.settings.recentlyViewedHeading || 'Recently Viewed Products')
      : (this.settings.customHeading || 'You may also like');
  }

  // Accordion management
  toggleAccordion(accordionId) {
    if (this.oneAccordionMode) {
      if (this.activeAccordion === accordionId) {
        this.activeAccordion = null;
      } else {
        this.activeAccordion = accordionId;
      }
    } else {
      if (this.activeAccordion === accordionId) {
        this.activeAccordion = null;
      } else {
        this.activeAccordion = accordionId;
      }
    }
    this.render();
  }

  isAccordionOpen(accordionId) {
    if (this.oneAccordionMode) {
      return this.activeAccordion === accordionId;
    }
    return this.activeAccordion === accordionId;
  }

  closeAccordion(accordionId) {
    if (this.activeAccordion === accordionId) {
      this.activeAccordion = null;
      this.render();
    }
  }

  // Free samples
  getEligibleTier() {
    if (!this.freeSamplesConfig.tiers?.length) return null;
    const cartValue = (this.data?.total_price || 0) / 100;
    const sorted = [...this.freeSamplesConfig.tiers].sort((a, b) => b.minimum_price - a.minimum_price);
    return sorted.find(tier => cartValue >= tier.minimum_price) || null;
  }

  getActiveTierMinPrice() {
    const cartValue = (this.data?.total_price || 0) / 100;
    const eligibleTier = this.getEligibleTier();
    if (eligibleTier) return eligibleTier.minimum_price;

    const sortedTiers = [...(this.freeSamplesConfig.tiers || [])].sort((a, b) => a.minimum_price - b.minimum_price);
    const nextTier = sortedTiers.find(tier => cartValue < tier.minimum_price);
    return nextTier ? nextTier.minimum_price : 0;
  }

  getAddedFromEligibleTier() {
    const eligibleTier = this.getEligibleTier();
    if (!eligibleTier) return 0;
    return this.addedSamples.filter(id => eligibleTier.products.some(p => p.id === id)).length;
  }

  canAddMoreSamples() {
    const eligibleTier = this.getEligibleTier();
    if (!eligibleTier) return false;
    return this.getAddedFromEligibleTier() < eligibleTier.quantity;
  }

  getSampleStatus() {
    const eligibleTier = this.getEligibleTier();
    if (!eligibleTier) return { message: '', remainingText: '' };

    const totalSamples = eligibleTier.quantity;
    const addedCount = this.getAddedFromEligibleTier();
    const remaining = Math.max(totalSamples - addedCount, 0);

    return {
      message: `You may choose ${totalSamples} Free Samples (${remaining} left)`,
      remainingText: `(${remaining} free samples left)`
    };
  }

  async addFreeSampleToCart(product) {
    if (!this.canAddMoreSamples() || this.isAnyLoading()) return;

    const variantId = product.variants.find(variant => variant.available)?.id;
    if (!variantId) return;

    const productIdStr = String(product.id);
    this.loadingProducts.add(productIdStr);
    this.recentlyAddedSample = product.id;
    this.render();

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { _sample: true },
        }),
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      await this.getCartAndUpdateSamples();
      setTimeout(() => { this.recentlyAddedSample = null; }, 500);

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.recentlyAddedSample = null;
    } finally {
      this.loadingProducts.delete(productIdStr);
      this.render();
    }
  }

  async getCartAndUpdateSamples() {
    try {
      const response = await fetch(`${Shopify.routes.root}cart.js`);
      const cartData = await response.json();

      this.updateSamplesStateFromCart(cartData);

      this.updateCartValues(cartData);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  isFreeSample(productId) {
    return this.freeSamplesConfig.tiers.some(tier =>
      tier.products.some(product => product.id === productId)
    );
  }

  updateSamplesStateFromCart(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) {
      this.addedSamples = [];
      return;
    }

    this.addedSamples = cartData.items
      .filter(item => this.isFreeSample(item.product_id || item.id))
      .map(item => item.product_id || item.id);
  }

  // GWP (Gift With Purchase)
  isEligibleForGWP() {
    return (this.data?.total_price || 0) >= this.gwpConfig.minimumPurchase;
  }

  getGWPMaxSelections() {
    const max = Number(this.gwpConfig.maxSelections);
    return Number.isFinite(max) && max > 0 ? Math.floor(max) : 1;
  }

  hasAnyGWPSelected() {
    return this.addedGWPCount > 0;
  }

  hasReachedGWPMax() {
    return this.addedGWPCount >= this.getGWPMaxSelections();
  }

  getGWPAmountAwayMessage() {
    const amountNeeded = ((this.gwpConfig.minimumPurchase - (this.data?.total_price || 0)) / 100).toFixed(2);
    return (this.gwpConfig.amountAwayText || 'Spend {amount} more for a free gift').replace(/{amount}/g, `${this.settings.currencySymbol}${amountNeeded}`);
  }

  getAvailableGWPProducts() {
    if (this.hasReachedGWPMax()) {
      return this.gwpConfig.products.filter(product => this.addedGWP.includes(product.id));
    }
    return this.gwpConfig.products;
  }

  async addGWPToCart(product) {
    if (this.hasReachedGWPMax() || this.isAnyLoading()) return;

    const variantId = product.variants.find(variant => variant.available)?.id;
    if (!variantId) return;

    const productIdStr = String(product.id);
    this.loadingProducts.add(productIdStr);
    this.recentlyAddedGWP = product.id;
    this.render();

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { _gwp: 'true' },
        }),
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      await this.getCartAndUpdateGWP();

      setTimeout(() => {
        this.closeAccordion('gwp');
        this.recentlyAddedGWP = null;
        this.render();
      }, 1000);

    } catch (error) {
      console.error('Error adding GWP to cart:', error);
      this.recentlyAddedGWP = null;
    } finally {
      this.loadingProducts.delete(productIdStr);
      this.render();
    }
  }

  async getCartAndUpdateGWP() {
    try {
      const response = await fetch(`${Shopify.routes.root}cart.js`);
      const cartData = await response.json();
      this.updateGWPStateFromCart(cartData);

      this.updateCartValues(cartData);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  isFreeGift(item) {
    const gwpFlag = item.properties && item.properties._gwp;
    const hasGWPProperty = gwpFlag === true || gwpFlag === 'true' || gwpFlag === 1 || gwpFlag === '1';
    const isFreePrice = item.final_line_price === 0 || item.discounted_price === 0 || item.price === 0;
    const hasLineDiscount = (item.line_level_total_discount || 0) > 0;
    const isGWPById = this.gwpConfig.products.some(product => product.id === (item.product_id || item.id));
    return isGWPById && (hasGWPProperty || isFreePrice || hasLineDiscount);
  }

  updateGWPStateFromCart(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) {
      this.addedGWP = [];
      this.addedGWPCount = 0;
      return;
    }

    const giftItems = cartData.items.filter(item => this.isFreeGift(item));
    this.addedGWP = giftItems.map(item => item.product_id || item.id);
    this.addedGWPCount = giftItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
  }

  // Discount codes
  loadDiscountCodesFromStorage() {
    try {
      const stored = localStorage.getItem('appliedDiscountCodes');
      const dateStored = localStorage.getItem('discountCodeAddedDate');

      if (!stored) {
        this.appliedCodes = [];
        return;
      }

      if (dateStored) {
        const addedDate = new Date(dateStored);
        const currentDate = new Date();
        const hoursDiff = (currentDate - addedDate) / (1000 * 60 * 60);
        if (hoursDiff > 48) {
          localStorage.removeItem('appliedDiscountCodes');
          localStorage.removeItem('discountCodeAddedDate');
          this.appliedCodes = [];
          return;
        }
      }

      this.appliedCodes = JSON.parse(stored);
    } catch (error) {
      console.error('Error reading discount codes from localStorage:', error);
      this.appliedCodes = [];
    }
  }

  setStoredDiscountCodes(codes) {
    try {
      if (codes.length > 0) {
        localStorage.setItem('appliedDiscountCodes', JSON.stringify(codes));
        if (!localStorage.getItem('discountCodeAddedDate')) {
          localStorage.setItem('discountCodeAddedDate', new Date().toISOString());
        }
      } else {
        localStorage.removeItem('appliedDiscountCodes');
        localStorage.removeItem('discountCodeAddedDate');
      }
    } catch (error) {
      console.error('Error storing discount codes to localStorage:', error);
    }
  }
  async refetchComparePrices(items) {
  if (!items || !items.length) return;
  
  await Promise.all(items.map(async (item) => {
    try {
      const productRes = await fetch(`/products/${item.handle}.js`);
      const product = await productRes.json();
      const variant = product.variants.find(v => v.id === item.variant_id);
      
      if (variant && variant.compare_at_price) {
        item.compare_at_price = variant.compare_at_price;
      }
    } catch (err) {
      console.warn('Failed to refetch compare price for', item.product_title);
    }
  }));
  
  this.render(); // Re-render with updated compare prices
}
  async applyDiscountCode(code) {
    if (!code.trim()) return;

  this.isApplyingDiscount = true;
  this.discountErrorMessage = '';
  this.render();

  try {
    let discountCodes = [...this.appliedCodes];
    const newCode = code.trim();

    if (!discountCodes.includes(newCode)) {
      discountCodes.push(newCode);
    }

    const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: discountCodes.join(',') })
    });

    const data = await response.json();

    const validCodes = data.discount_codes.filter(code => code.applicable).map(code => code.code);
    const newlyInvalidCodes = data.discount_codes
      .filter(code => !code.applicable && discountCodes.includes(code.code))
      .map(code => code.code);

    if (newlyInvalidCodes.length > 0) {
      this.discountErrorMessage = `Failed to apply discount code: ${newlyInvalidCodes.join(', ')}`;
    } else {
      this.discountCodeInput = '';
    }

    this.setStoredDiscountCodes(validCodes);
    this.appliedCodes = validCodes;

    this.updateCartValues(data);
    
    // Refetch compare prices after applying discount
    if (data.items && data.items.length > 0) {
      await this.refetchComparePrices(data.items);
    }

  } catch (error) {
    console.error('Failed to apply discount code:', error);
    this.discountErrorMessage = 'Failed to apply discount code. Please try again.';
  } finally {
    this.isApplyingDiscount = false;
    this.render();
  }
  }

  async removeDiscountCode(codeToRemove) {
   try {
    let discountCodes = this.appliedCodes.filter(code => code !== codeToRemove);

    const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: discountCodes.join(',') })
    });

    const data = await response.json();

    this.setStoredDiscountCodes(discountCodes);
    this.appliedCodes = discountCodes;

    // Update cart values first
    this.updateCartValues(data);
    
    // IMPORTANT: Refetch compare prices after discount removal
    if (data.items && data.items.length > 0) {
      await this.refetchComparePrices(data.items);
    }

  } catch (error) {
    console.error('Failed to remove discount code:', error);
    this.discountErrorMessage = 'Failed to remove discount code. Please try again.';
    this.render();
  }
  }

  getDiscountAppliedLabel() {
    const codesString = this.appliedCodes.join(', ');
    return (this.settings.discountAppliedLabelTemplate || '({codes}) Discount Applied').replace(/{codes}/g, codesString);
  }

  // Order note
  async saveOrderNote(note) {
    this.isOrderNoteUpdating = true;
    this.render();

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ note: note }),
      });

      const data = await response.json();
      this.orderNote = data.note || '';
      this.hasUnsavedChanges = false;
      this.updateCartValues(data);

    } catch (error) {
      console.error('Error saving order note:', error);
    } finally {
      this.isOrderNoteUpdating = false;
      this.render();
    }
  }

  async clearOrderNote() {
    this.orderNote = '';
    await this.saveOrderNote('');
  }

  // Shipping rates
  async fetchShippingRates(address) {
    if (!address.country || !address.zip) {
      alert('Please fill in Country and ZIP/Postal Code');
      return;
    }

    const shippingAddress = {
      country: address.country,
      province: address.province || '',
      zip: address.zip
    };

    try {
      const response = await fetch('/cart/shipping_rates.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shipping_address: shippingAddress })
      });

      const data = await response.json();

      if (data.shipping_rates) {
        this.shippingRates = data.shipping_rates;
        this.saveShippingToLocalStorage(shippingAddress, data.shipping_rates);
        this.render();
      } else {
        alert('No shipping rates available.');
      }
    } catch (error) {
      console.error('Error fetching shipping rates:', error);
    }
  }

  saveShippingToLocalStorage(address, rates) {
    const shopDomain = this.settings.shopDomain || 'default';
    const shopIdentifier = shopDomain.split('.')[0];

    try {
      localStorage.setItem(`${shopIdentifier}_shopify_shipping_rates`, JSON.stringify({
        rates: rates,
        timestamp: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
      }));
      localStorage.setItem(`${shopIdentifier}_shopify_shipping_address`, JSON.stringify({
        address: address,
        timestamp: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }

  loadShippingFromLocalStorage() {
    const shopDomain = this.settings.shopDomain || 'default';
    const shopIdentifier = shopDomain.split('.')[0];

    try {
      const ratesData = localStorage.getItem(`${shopIdentifier}_shopify_shipping_rates`);
      const addressData = localStorage.getItem(`${shopIdentifier}_shopify_shipping_address`);

      if (ratesData && addressData) {
        const parsedRates = JSON.parse(ratesData);
        const parsedAddress = JSON.parse(addressData);

        const now = new Date();
        const expiresAt = new Date(parsedRates.expiresAt);

        if (expiresAt > now) {
          this.shippingRates = parsedRates.rates;
          if (parsedAddress.address) {
            this.formCountry = parsedAddress.address.country || '';
            this.formProvince = parsedAddress.address.province || '';
            this.formZip = parsedAddress.address.zip || '';
          }
          return true;
        }
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
    return false;
  }

  getShippingRange() {
    if (!this.shippingRates.length) return null;
    const prices = this.shippingRates.map(rate => Math.round(parseFloat(rate.price) * 100));

    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const isRange = prices.length > 1 && min !== max;

    return { min, max, isRange, count: prices.length };
  }

  // Loading state helpers
  isLoading(productId) {
    return this.loadingProducts.has(String(productId));
  }

  isAnyLoading() {
    return this.loadingProducts.size > 0;
  }

  // Free shipping
  getFreeShippingPercent() {
    const threshold = parseInt(this.settings.freeShippingThreshold || 0) * 100;
    if (threshold <= 0) return 0;
    const totalPrice = this.data?.total_price || 0;
    const percent = (totalPrice * 100) / threshold;
    return Math.min(100, Math.round(percent));
  }

  getFreeShippingLabel() {
    const threshold = (this.settings.freeShippingThreshold || 0) * 100;
    const totalPrice = this.data?.total_price || 0;

    if (totalPrice >= threshold) {
      return 'Free shipping activated!';
    }

    const remaining = ((threshold - totalPrice) / 100).toFixed(2);
    return `You're ${this.settings.currencySymbol}${remaining} away from FREE Shipping`;
  }

  // Checkout handler
  handleCheckout(e) {
    if (this.orderNote && this.hasUnsavedChanges) {
      this.saveOrderNote(this.orderNote);
    }
  }

  // Render method
  render() {
    const cartUrl = `${window.Shopify.routes.root}cart`;
    const checkoutUrl = `${window.Shopify.routes.root}checkout`;
    const hasItems = this.data && this.data.items?.length > 0;
    const productsToDisplay = this.getProductsToDisplay();

    this.innerHTML = `
      <div
        class="mini-cart h-full flex flex-col ${this.cartIsBlocked ? 'cartIsBlocked' : 'cartIsUnBlocked'}"
        aria-labelledby="mini-cart__title"
        tabindex="${this.cartIsOpen ? '1' : '-1'}"
        ${!this.cartIsOpen ? 'inert' : ''}
      >
        <!-- HEADER -->
        <header class="mini-cart__header px-4 ">
          <div class="mini-cart__header-inner border-color flex justify-between items-center border-b">
            <span id="mini-cart__title" class="mini-cart__title cart-drawer-heading ${this.settings.cartheading} text-xs">
              ${this.settings.cartName || 'Cart'} ${hasItems ? `<span>(${this.data.item_count})</span>` : ''}
            </span>
            <button
              class="js-mini-cart-close h-12 w-12 flex items-center justify-center"
              type="button"
              aria-label="${this.settings.closeActionText || 'Close'} ${(this.settings.cartName || 'cart').toLowerCase()}"
            >
              ${this.renderCloseIcon()}
            </button>
          </div>
          ${hasItems && this.settings.freeShippingEnabled ? `
            <div class="mini-cart__dtfs-wrap py-4 border-b">
              <label for="mini-cart__dtfs" class=" cart-drawer-heading body-4 block text-center leading-[1.2] text-[0.688rem] font-medium mb-[0.438rem]">
                ${this.getFreeShippingLabel()}
              </label>
              <progress id="mini-cart__dtfs" class="w-full mini-cart__dtfs" value="${this.getFreeShippingPercent()}" max="100"></progress>
            </div>
          ` : ''}
        </header>

        ${this.initialLoading ? `
          <section class="mini-cart__merge-section">
            <div class="text-center">
              <div class="inline-block"></div>
              <p class="text-sm font-bold">${this.settings.cartLoadingState || 'Loading cart...'}</p>
            </div>
          </section>
        ` : `
          <div class="mini-cart__merge-section">
            ${hasItems ? `
              ${this.tableLayout ? `
                <div class="mini-cart__table-header">
                  <span>Product</span>
                  <span>Price</span>
                  <span>Quantity</span>
                  <span>Total</span>
                </div>
              ` : ''}
              <section class="mini-cart__items pl-4  pr-5.5 flex flex-wrap">
                ${this.data.items.map(item => this.renderCartItem(item)).join('')}
              </section>
            ` : `
              <section class="mini-cart__is-empty flex items-center flex-col">
                <div class="mini-cart__is-empty-inner px-4 pt-4">
                  <h2 class="mini_cart_body_text ${this.settings.emptyCartBodyText}">${this.settings.cartEmptyMessage || 'Your cart is empty'}</h2>
                </div>
                <button class="button mini-cart__empty-button ${this.settings.checkout_button_text}  js-close-mini-cart m-4">
                  ${this.settings.cartEmptyButtonText || 'Return to shop'}
                </button>
                ${productsToDisplay.length ? `
                  <div class="mini-cart__recently-viewed text-center pb-4 px-4">
                    <h3 class="mb-4 mini_cart_body_text ${this.settings.emptyCartBodyText}">${this.getProductsHeading()}</h3>
                    <div class="grid grid-cols-2 gap-4">
                      ${productsToDisplay.map(product => this.renderRecentlyViewedProduct(product)).join('')}
                    </div>
                  </div>
                ` : ''}
              </section>
            `}

            ${this.settings.upshellEnabled && hasItems && this.displayedUpsellProducts.length ? `
              <section class="mini-cart__upsell">
                <header class="mini-cart__upsell-header mb-2">
                  <h2>${this.settings.upsellHeading || 'Purchased frequently with'}</h2>
                </header>
                <div class="mini-cart__upsell-items flex overflow-x-auto gap-2.5 pb-4">
                  ${this.displayedUpsellProducts.map(product => this.renderUpsellItem(product)).join('')}
                </div>
                ${this.initialDisplayCount < this.upsellProducts.length ? `
                  <button class="js-show-more-upsell">${this.settings.showMoreText || 'Show more'}</button>
                ` : ''}
              </section>
            ` : ''}
          </div>
        `}

        <!-- FOOTER -->
        ${!this.initialLoading && hasItems ? `
          <footer class="mini-cart__footer border-color flex flex-col mx-4 border-t">
            <div class="mini-cart__footer-utils">
              ${this.renderFreeSamples()}
              ${this.renderGWP()}
              ${this.renderOrderNote()}
              ${this.renderDiscountCode()}
              ${this.renderShippingRates()}
            </div>
            <div class="mini-cart__footer-totals">
              ${this.renderTotals()}
              <div class="text-center py-4">
                <div class="mini-checkout-footer js-mini-cart-footer">
                  <a href="${checkoutUrl}" class="mini-cart__button button ${this.settings.checkout_button_text} checkout-button w-full js-checkout-link" aria-label="Links to ${this.settings.language?.checkoutButton || 'Checkout'}">
                    ${this.settings.language?.checkoutButton || 'CONTINUE TO CHECKOUT'}
                    ${this.settings.showPriceCheckoutBtn ? `<span class="checkout-btn-price"> - ${formatMoney(this.data?.total_price)}</span>` : ''}
                  </a>
                </div>
                ${this.settings.showEmptyCart ? `
                  <button class="empty-cart-button body-4 underlined-link--always-underlined mt-4 text-xs cursor-pointer js-empty-cart">
                    ${this.settings.emptyCartActionText || 'Empty cart'}
                  </button>
                ` : ''}
              </div>
            </div>
          </footer>
        ` : ''}
      </div>
    `;

    this.attachEventListeners();
  }

  renderCloseIcon() {
    return `<svg width="13" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M5.94807 7.31355L10.7271 12.0926L11.4342 11.3855L6.65518 6.60645L11.4342 1.82743L10.7271 1.12033L5.94807 5.89934L0.827589 0.778857L0.120482 1.48596L5.24096 6.60644L0.120481 11.7269L0.827588 12.434L5.94807 7.31355Z" fill="#3F3844"/>
    </svg>`;
  }

  renderDeleteIcon() {
    return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M5.5 5.5H6.5V10.5H5.5V5.5ZM7.5 5.5H8.5V10.5H7.5V5.5Z" fill="currentColor"/>
      <path d="M2 2.5V3.5H3V12.5C3 12.7652 3.10536 13.0196 3.29289 13.2071C3.48043 13.3946 3.73478 13.5 4 13.5H10C10.2652 13.5 10.5196 13.3946 10.7071 13.2071C10.8946 13.0196 11 12.7652 11 12.5V3.5H12V2.5H2ZM4 12.5V3.5H10V12.5H4ZM5.5 0.5H8.5V1.5H5.5V0.5Z" fill="currentColor"/>
    </svg>`;
  }

  renderLoadingIcon() {
    return `<svg class="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>`;
  }

  renderMinusIcon() {
    return `<svg width="10" height="2" viewBox="0 0 10 2" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M0 1H10" stroke="currentColor" stroke-width="1.5"/>
    </svg>`;
  }

  renderPlusIcon() {
    return `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M5 0V10M0 5H10" stroke="currentColor" stroke-width="1.5"/>
    </svg>`;
  }

  renderChevronIcon(rotated = false) {
    return `<svg class="w-4 h-4 transition-transform duration-200 ${rotated ? 'rotate-180' : 'rotate-0'}" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
    </svg>`;
  }

  renderGiftIcon() {
    return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="20 12 20 22 4 22 4 12"></polyline>
      <rect x="2" y="7" width="20" height="5"></rect>
      <line x1="12" y1="22" x2="12" y2="7"></line>
      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path>
      <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path>
    </svg>`;
  }

  renderDiscountIcon() {
    return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <line x1="19" y1="5" x2="5" y2="19"></line>
      <circle cx="6.5" cy="6.5" r="2.5"></circle>
      <circle cx="17.5" cy="17.5" r="2.5"></circle>
    </svg>`;
  }

  renderNoteIcon() {
    return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <line x1="16" y1="13" x2="8" y2="13"></line>
      <line x1="16" y1="17" x2="8" y2="17"></line>
    </svg>`;
  }

  renderShippingIcon() {
    return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <rect x="1" y="3" width="15" height="13"></rect>
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
      <circle cx="5.5" cy="18.5" r="2.5"></circle>
      <circle cx="18.5" cy="18.5" r="2.5"></circle>
    </svg>`;
  }

  renderCartItem(item) {
    const isLoading = this.isLoading(item.id);
    const quantityRule = this.quantityRuleMap[item.id] || { min: 1, max: null, increment: 1 };
    const isFreeSample = item.price === 0 || item.product_title.toLowerCase().includes('free');
    const isDiscountFree = item.discounted_price === 0;
    const isMaxReached = quantityRule.max !== null && item.quantity >= quantityRule.max;
    const freeItemText = this.settings.freeItemText || 'Free';
    const isProductWithCustomOptions = item.options_with_values?.[0]?.name !== 'Title' && item.options_with_values?.[0]?.value !== 'Default Title';

    const freeSampleClass = (item.product_title.toLowerCase().includes('free') || item.product_title.toLowerCase().includes('sample')) && item.price === 0
      ? 'mini-cart__item--free-sample' : '';

    return `
      <div class="mini-cart__item ${freeSampleClass}">
        ${isLoading ? `<span class="mini-cart__item-loading">${this.renderLoadingIcon()}</span>` : ''}

        <div class="mini-cart__item-inner flex gap-3 sm:gap-3 justify-between" data-free-sample="${isFreeSample}">
          <figure class="mini-cart__item-fig w-16 sm:w-24 md:w-32 lg:w-40 mb-auto shrink-0">
            <a href="${!isFreeSample ? item.url : '#'}" aria-label="${item.title}">
              <img
                srcset="${getCroppedImageUrl(item.featured_image?.url, [240, 320, 414, 640])}"
                alt="${item.featured_image?.title || item.title}"
                width="${item.featured_image?.width || 160}"
                height="${item.featured_image?.height || 200}"
                class="block object-cover mini-cart__item-img h-full w-full aspect-square"
                loading="lazy"
              />
            </a>
          </figure>

          <div class="flex-1 min-w-0">
            ${!isFreeSample ? `
              <a href="${item.url}" aria-label="${item.title}">
                <h3 class="mini-cart__item-title mini-cart-product-color  body-4 text-sm sm:text-base wrap-break-word">${item.product_title}</h3>
              </a>
            ` : `
              <h3 class="mini-cart__item-title  mini-cart-product-color body-4 text-sm sm:text-base wrap-break-word">${item.product_title}</h3>
            `}

            ${isProductWithCustomOptions ? `
              <ul class="mini-cart__item-options text-xs ${item.options_with_values.filter(opt => opt.value !== 'Default Title').length > 0 ? 'pt-2' : ''}">
                ${item.options_with_values.filter(opt => opt.value !== 'Default Title').map(option => `
                  <li class="mini-cart__item-option">
                    <span class="mini-cart__item-option-name mini-cart-product-color body-4">${option.name}:-</span>
                    <span class="mini-cart__item-option-value mini-cart-product-color  body-5">${option.value}</span>
                  </li>
                `).join('')}
              </ul>
            ` : ''}
             <button
              type="button"
              aria-label="Delete ${item.title}"
              class="mini-cart__delete-btn js-remove-item desktop-section__remove"
              data-item-key="${item.key}"
              data-item-id="${item.id}"
            >
              <span class="mini-cart__remove-text">${this.settings.language?.removeFromCart || 'Remove'}</span>
              <span class="mini-cart__remove-icon">${this.renderDeleteIcon()}</span>
            </button>

            ${!this.tableLayout ? `
              <div class="pt-2 flex h-fit items-center gap-2 sm:gap-3 quantity-selector-wrapper">
                ${!isFreeSample ? `
                  <form class="mini-cart__qty-change ${isDiscountFree ? 'opacity-50 pointer-events-none' : ''}">
                    <fieldset ${isLoading || isDiscountFree ? 'disabled' : ''} class="flex items-center w-fit relative h-[1.625rem] max-w-[5rem]">
                      <button
                        type="button"
                        ${isDiscountFree ? 'disabled' : ''}
                        aria-label="${this.settings.decreaseQuantityText || 'Decrease quantity for'} ${item.title}"
                        class="cart__decrement_btn mini-cart-product-color  cursor-pointer inline-flex items-center justify-center h-full w-7 sm:w-8 js-qty-decrease"
                        data-item-key="${item.key}"
                        data-item-id="${item.id}"
                        data-new-qty="${item.quantity - quantityRule.increment}"
                      >
                        ${this.renderMinusIcon()}
                        <span class="sr-only">${this.settings.decreaseQuantityText || 'Decrease quantity for'} ${item.title}</span>
                      </button>
                      <label class="sr-only" for="qty-${item.id}">${this.settings.quantityLabelText || 'Quantity of'} ${item.title}</label>
                      <input
                        id="qty-${item.id}"
                        class="body-5 text-center  mini-cart-product-color appearance-none h-full bg-background w-7 sm:w-8 text-sm mini-cart__increment_qty"
                        type="tel"
                        value="${item.quantity}"
                        min="${quantityRule.min}"
                        disabled
                      />
                      <button
                        type="button"
                        ${isFreeSample || isDiscountFree || isMaxReached ? 'disabled' : ''}
                        aria-label="${this.settings.increaseQuantityText || 'Increase quantity for'} ${item.title}"
                        class="mini-cart__increment_btn mini-cart-product-color cursor-pointer inline-flex items-center justify-center h-7 sm:h-8 w-7 sm:w-8 js-qty-increase${isMaxReached ? ' is-disabled opacity-50 pointer-events-none' : ''}"
                        data-item-key="${item.key}"
                        data-item-id="${item.id}"
                        data-new-qty="${item.quantity + quantityRule.increment}"
                      >
                        ${this.renderPlusIcon()}
                        <span class="sr-only">${this.settings.increaseQuantityText || 'Increase quantity for'} ${item.title}</span>
                      </button>
                    </fieldset>
                  </form>
                ` : ''}
                <button
                  type="button"
                  aria-label="Delete ${item.title}"
                  class="mini-cart__delete-btn js-remove-item"
                  data-item-key="${item.key}"
                  data-item-id="${item.id}"
                >
                  <span class="sr-only">${this.settings.language?.removeFromCart || 'Remove'}</span>
                  ${this.renderDeleteIcon()}
                </button>
              </div>
            ` : `
              <!-- mobile col2: title+options+price+qty (hidden on desktop via CSS) -->
              <div class="mini-cart__mobile-info">
                <span class="mini-cart__mobile-price">
                  <b class="mini-cart-product-colors body-5 price-style">
                    ${formatMoney(item.discounted_price || item.price)}
                  </b>
                  ${(!item.discounted_price || item.discounted_price >= item.price) ? `
                    ${item.compare_at_price && item.compare_at_price > item.price ? `
                      <s class="mini-cart-product-color body-5 price-style">
                        ${formatMoney(item.compare_at_price)}
                      </s>
                    ` : ''}
                  ` : ''}
                  ${(item.discounted_price && item.discounted_price < item.price) ? `
                    <s class="mini-cart-product-color body-5">
                      ${formatMoney(item.price)}
                    </s>
                  ` : ''}
                </span>
                <form class="mini-cart__qty-change mini-cart__mobile-qty ${isDiscountFree ? 'opacity-50 pointer-events-none' : ''}">
                  <fieldset ${isLoading || isDiscountFree ? 'disabled' : ''} class="flex items-center w-fit relative h-[1.625rem] max-w-[5rem]">
                    <button
                      type="button"
                      ${isDiscountFree ? 'disabled' : ''}
                      aria-label="${this.settings.decreaseQuantityText || 'Decrease quantity for'} ${item.title}"
                      class="cart__decrement_btn mini-cart-product-color cursor-pointer inline-flex items-center justify-center h-full w-7 sm:w-8 js-qty-decrease"
                      data-item-key="${item.key}"
                      data-item-id="${item.id}"
                      data-new-qty="${item.quantity - quantityRule.increment}"
                    >
                      ${this.renderMinusIcon()}
                    </button>
                    <label class="sr-only" for="qty-mob-${item.id}">${this.settings.quantityLabelText || 'Quantity of'} ${item.title}</label>
                    <input
                      id="qty-mob-${item.id}"
                      class="body-5 text-center mini-cart-product-color appearance-none h-full bg-background w-7 sm:w-8 text-sm mini-cart__increment_qty"
                      type="tel"
                      value="${item.quantity}"
                      min="${quantityRule.min}"
                      disabled
                    />
                    <button
                      type="button"
                      ${isFreeSample || isDiscountFree || isMaxReached ? 'disabled' : ''}
                      aria-label="${this.settings.increaseQuantityText || 'Increase quantity for'} ${item.title}"
                      class="mini-cart__increment_btn mini-cart-product-color cursor-pointer inline-flex items-center justify-center h-7 sm:h-8 w-7 sm:w-8 js-qty-increase${isMaxReached ? ' is-disabled opacity-50 pointer-events-none' : ''}"
                      data-item-key="${item.key}"
                      data-item-id="${item.id}"
                      data-new-qty="${item.quantity + quantityRule.increment}"
                    >
                      ${this.renderPlusIcon()}
                    </button>
                  </fieldset>
                </form>
              </div>
              <!-- mobile col3: remove + total (hidden on desktop via CSS) -->
              <div class="mini-cart__mobile-col3">
                <button
                  type="button"
                  aria-label="Delete ${item.title}"
                  class="mini-cart__delete-btn js-remove-item mini-cart__remove-text-btn"
                  data-item-key="${item.key}"
                  data-item-id="${item.id}"
                >
                  <span class="mini-cart__remove-text">${this.settings.language?.removeFromCart || 'Remove'}</span>
                  <span class="mini-cart__remove-icon">${this.renderDeleteIcon()}</span>
                </button>
                <span class="mini-cart__mobile-total">
                  <b class="mini-cart-product-colors body-5 price-style">
                    ${formatMoney((item.discounted_price || item.price) * item.quantity)}
                  </b>
                </span>
              </div>
            `}

            ${isMaxReached && quantityRule.tracked ? `
              <div class="mini-cart__stock-msg text-xs mt-1" style="color:#b91c1c;">
                Already at max stock in cart (${item.quantity} of ${quantityRule.max})
              </div>
            ` : ''}

            ${quantityRule.min > 1 && this.settings.quantityNoteText ? `
              <div class="my-2">
                ${this.settings.quantityNoteText.replace('{increment}', quantityRule.increment).replace('{min}', quantityRule.min)}
              </div>
            ` : ''}
          </div>

          <div class="w-auto sm:w-[20%] md:w-[18%] lg:w-[15%] flex flex-col price_section items-end shrink-0">
          ${this.tableLayout ? `
            <span class="mini-cart__item-unit-price text-xs sm:text-sm text-end">
              <b class="block wrap-break-word mini-cart-product-colors body-5 price-style">
                ${formatMoney(item.discounted_price || item.price)}
              </b>
              ${(!item.discounted_price || item.discounted_price >= item.price) ? `
                ${item.compare_at_price && item.compare_at_price > item.price ? `
                  <s class="block pt-1 text-[0.65rem] mini-cart-product-color body-5 sm:text-xs price-style">
                    ${formatMoney(item.compare_at_price)}
                  </s>
                ` : ''}
              ` : ''}
              ${(item.discounted_price && item.discounted_price < item.price) ? `
                <s class="block pt-1 text-[0.65rem] mini-cart-product-color body-5 sm:text-xs">
                  ${formatMoney(item.price)}
                </s>
              ` : ''}
            </span>
            <form class="mini-cart__qty-change ${isDiscountFree ? 'opacity-50 pointer-events-none' : ''}">
              <fieldset ${isLoading || isDiscountFree ? 'disabled' : ''} class="flex items-center w-fit relative h-[1.625rem] max-w-[5rem]">
                <button
                  type="button"
                  ${isDiscountFree ? 'disabled' : ''}
                  aria-label="${this.settings.decreaseQuantityText || 'Decrease quantity for'} ${item.title}"
                  class="cart__decrement_btn mini-cart-product-color cursor-pointer inline-flex items-center justify-center h-full w-7 sm:w-8 js-qty-decrease"
                  data-item-key="${item.key}"
                  data-item-id="${item.id}"
                  data-new-qty="${item.quantity - quantityRule.increment}"
                >
                  ${this.renderMinusIcon()}
                  <span class="sr-only">${this.settings.decreaseQuantityText || 'Decrease quantity for'} ${item.title}</span>
                </button>
                <label class="sr-only" for="qty-tbl-${item.id}">${this.settings.quantityLabelText || 'Quantity of'} ${item.title}</label>
                <input
                  id="qty-tbl-${item.id}"
                  class="body-5 text-center mini-cart-product-color appearance-none h-full bg-background w-7 sm:w-8 text-sm mini-cart__increment_qty"
                  type="tel"
                  value="${item.quantity}"
                  min="${quantityRule.min}"
                  disabled
                />
                <button
                  type="button"
                  ${isFreeSample || isDiscountFree || isMaxReached ? 'disabled' : ''}
                  aria-label="${this.settings.increaseQuantityText || 'Increase quantity for'} ${item.title}"
                  class="mini-cart__increment_btn mini-cart-product-color cursor-pointer inline-flex items-center justify-center h-7 sm:h-8 w-7 sm:w-8 js-qty-increase${isMaxReached ? ' is-disabled opacity-50 pointer-events-none' : ''}"
                  data-item-key="${item.key}"
                  data-item-id="${item.id}"
                  data-new-qty="${item.quantity + quantityRule.increment}"
                >
                  ${this.renderPlusIcon()}
                  <span class="sr-only">${this.settings.increaseQuantityText || 'Increase quantity for'} ${item.title}</span>
                </button>
              </fieldset>
            </form>
            <div class="flex flex-1 justify-end price-section__total-wrap">
              <span class="mini-cart__item-total-price text-xs sm:text-sm text-end">
                <b class="block wrap-break-word mini-cart-product-colors body-5 price-style">
                  ${formatMoney((item.discounted_price || item.price) * item.quantity)}
                </b>
                ${(!item.discounted_price || item.discounted_price >= item.price) ? `
                  ${item.compare_at_price && item.compare_at_price > item.price ? `
                    <s class="block pt-1 text-[0.65rem] mini-cart-product-color body-5 sm:text-xs price-style">
                      ${formatMoney(item.compare_at_price * item.quantity)}
                    </s>
                  ` : ''}
                ` : ''}
                ${(item.discounted_price && item.discounted_price < item.price) ? `
                  <s class="block pt-1 text-[0.65rem] mini-cart-product-color body-5 sm:text-xs">
                    ${formatMoney(item.price * item.quantity)}
                  </s>
                ` : ''}
              </span>
            </div>
            <button
              type="button"
              aria-label="Delete ${item.title}"
              class="mini-cart__delete-btn js-remove-item price-section__remove"
              data-item-key="${item.key}"
              data-item-id="${item.id}"
            >
              <span class="mini-cart__remove-text">${this.settings.language?.removeFromCart || 'Remove'}</span>
              <span class="mini-cart__remove-icon">${this.renderDeleteIcon()}</span>
            </button>
          ` : `
          <div class="mini-cart__item-price flex flex-1 justify-end">
            <span class="mini-cart__item-total-price text-xs sm:text-sm text-end">
              <b class="block wrap-break-word mini-cart-product-colors body-5 price-style">
                ${formatMoney((item.discounted_price || item.price) * item.quantity)}
              </b>

              ${(!item.discounted_price || item.discounted_price >= item.price) ? `
                ${item.compare_at_price && item.compare_at_price > item.price ? `
                  <s class="block  pt-1 text-[0.65rem] mini-cart-product-color body-5 sm:text-xs price-style">
                    ${formatMoney(item.compare_at_price * item.quantity)}
                  </s>
                ` : ''}
              ` : ''}

              ${(item.discounted_price && item.discounted_price < item.price) ? `
                <s class="block  pt-1 text-[0.65rem] mini-cart-product-color body-5 sm:text-xs">
                  ${formatMoney(item.price * item.quantity)}
                </s>
              ` : ''}
            </span>
          </div>
          `}
        </div>
        </div>
      </div>
    `;
  }

  renderUpsellItem(product) {
    const isLoading = this.isLoading(product.id);
    const isAnyLoading = this.isAnyLoading();
    const selectedVariantId = this.selectedVariantIds[product.id] || (product.variants?.[0]?.id);
    const hasMultipleVariants = !(product.variants.length === 1 && product.variants[0].title === 'Default Title');
    const minQty = product.variants?.[0]?.quantity_rule?.min ?? 1;

    return `
      <div class="flex flex-col h-full mini-cart__upsell-item border gap-2" style="min-width: ${100 / (this.settings.upsellSlidesPerView || 2.5)}%">
        ${!product.available ? `
          <div class="mb-2 text-red-600">
            <strong>${this.settings.soldOutText || 'Sold out'}</strong>
          </div>
        ` : ''}

        <figure class="flex-1 mini-cart__upsell-fig">
          <a href="${product.url}" aria-label="Permalink to ${product.title}">
            <img
              class="object-cover h-full w-full mini-cart__upsell-img aspect-square"
              srcset="${getCroppedImageUrl(product.featured_image, [120, 160, 240, 320])}"
              src="${getCroppedImageUrl(product.featured_image, 160)}"
              alt="${product.title}"
              width="160"
              height="200"
              loading="lazy"
            />
          </a>
        </figure>

        <div class="text-xs flex-1 flex flex-col justify-between mini-cart__upsell-desc">
          <div>
            <h3><a href="${product.url}">${product.title}</a></h3>
          </div>
          <p>${formatMoney(product.price)}</p>
        </div>

        <form class="grid gap-2 js-upsell-form" data-product-id="${product.id}">
          <input type="hidden" value="${selectedVariantId}">
          ${hasMultipleVariants ? `
            <select
              name="id"
              id="productSelect--${product.id}"
              class="w-full transition-opacity ${isAnyLoading && !isLoading ? 'opacity-50' : ''}"
              ${isLoading || isAnyLoading ? 'disabled' : ''}
            >
              ${product.variants.map(variant => `
                <option value="${variant.id}" ${!variant.available ? 'disabled' : ''} ${variant.id === selectedVariantId ? 'selected' : ''}>
                  ${variant.title} - ${formatMoney(variant.price)}
                </option>
              `).join('')}
            </select>
          ` : `
            <input type="hidden" name="id" value="${product.variants[0].id}" ${!product.variants[0].available ? 'disabled' : ''}>
          `}

          <div class="items-center space-x-2 sr-only">
            <label for="Quantity--${product.id}" class="text-sm">Quantity:</label>
            <input type="number" id="Quantity--${product.id}" name="quantity" value="${minQty}" min="${minQty}"/>
          </div>

          <button
            type="submit"
            name="add"
            class="mini-cart__button transition-opacity button w-[70%] ${isLoading || isAnyLoading ? 'cursor-not-allowed' : ''} ${isAnyLoading && !isLoading ? '!opacity-50' : ''}"
            ${isLoading || isAnyLoading ? 'disabled' : ''}
          >
            ${isLoading ? (this.settings.itemIsAddingText || 'Adding...') : (this.settings.addtoBagText || 'Add to cart')}
          </button>
        </form>
      </div>
    `;
  }
  
  renderRecentlyViewedProduct(product) {
      let price = Number(product.price);

      let priceOutput = price === 0
        ? this.settings.currencySymbol + 'POA'
        : this.settings.currencySymbol + price.toLocaleString() + (this.settings.priceCustomText ? ' ' + this.settings.priceCustomText : '');
    const productColor = product.metafields?.custom?.product_type_color?.value || '#000';
    return `
     <a href="/products/${product.handle}" class="resource-card resource-card-layout" data-resource-type="product">

      <div class="resource-card__media" style="--resource-card-aspect-ratio: 4/5;">
        <img
          src="${this.getShopifyImageUrl(product.featured_image, 600)}"
          srcset="
            ${this.getShopifyImageUrl(product.featured_image, 200)} 200w,
            ${this.getShopifyImageUrl(product.featured_image, 400)} 400w,
            ${this.getShopifyImageUrl(product.featured_image, 600)} 600w,
            ${this.getShopifyImageUrl(product.featured_image, 800)} 800w
          "
          sizes="(max-width: 640px) 50vw, 25vw"
          alt="${product.title}"
          class="resource-card__image"
          loading="lazy"
          width="400"
          height="500"
        />
      </div>

      <div class="resource-card__content">
        <p class="resource-card__title paragraph"  style="color:${productColor}">
          ${product.title}
        </p>

         <div class="resource-card__price price">
    ${
      product.compare_at_price && product.compare_at_price > product.price
        ? `
        <span class="price__sale">
          <span class="price__current">
            ${this.settings.currencySymbol}${Number(product.price).toLocaleString()}
          </span>
        </span>
        `
        : `
        <span class="price__regular">
            ${priceOutput} 
        </span>

        <span class="price__compare line-through opacity-70 ml-2">
            ${product.compare_at_price 
            ? `${this.settings.currencySymbol}${Number(product.compare_at_price).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`
            : ''
          }
        </span>
        `
    }

  </div>
      </div>

    </a>
    `;
  }

  renderFreeSamples() {
    if (!this.freeSamplesConfig.active || !this.data?.item_count) return '';

    const isOpen = this.isAccordionOpen('free-samples');
    const eligibleTier = this.getEligibleTier();
    const activeTierMinPrice = this.getActiveTierMinPrice();
    const cartValue = (this.data?.total_price || 0) / 100;
    const isEligible = cartValue >= activeTierMinPrice;
    const sampleStatus = this.getSampleStatus();
    const canAddMore = this.canAddMoreSamples();
    const eligibleTierProducts = eligibleTier?.products.filter(p => !this.addedSamples.includes(p.id)) || [];

    const amountNeeded = (activeTierMinPrice - cartValue).toFixed(2);
    const amountAwayMessage = (this.settings.freeSampleAmountAwayText || "You're {amount} away from FREE Samples").replace(/{amount}/g, `$${amountNeeded}`);

    return `
      <div class="accordion-item  cart-drawer-heading border-color border-b">
        <h3>
          <button
            aria-expanded="${isOpen}"
            aria-controls="free-samples-panel"
            id="free-samples-header"
            class="flex items-center justify-between cart-drawer-heading w-full text-left py-4 cursor-pointer js-accordion-toggle"
            data-accordion="free-samples"
            ${!isEligible ? 'disabled' : ''}
          >
            <div class="flex items-center body-3 gap-2 justify-between w-[calc(100%-1rem)] text-left">
              <span class="flex items-center relative gap-2">
                ${this.settings.showAccordionIcons ? `<span class="mini-cart__util-icon">${this.renderGiftIcon()}</span>` : ''}
                ${this.freeSamplesConfig.heading || 'Free Samples'} <span class="text-xs">${sampleStatus.remainingText}</span>
              </span>
              ${!isEligible ? `
                <span class="text-xs font-normal text-gray-500">${amountAwayMessage}</span>
              ` : `
                <span class=" body-6 mini-cart__truncated-preview-text">${this.freeSamplesConfig.eligibleText || "You're eligible for FREE Samples"}</span>
              `}
            </div>
            ${this.renderChevronIcon(isOpen)}
          </button>
        </h3>
        ${isOpen ? `
          <button class="w-full h-full fixed inset-0 z-5 js-accordion-overlay" data-accordion="free-samples" style="background-color: var(--global-mask-color); backdrop-filter: var(--global-mask-blur);">
            <span class="sr-only">close</span>
          </button>
        ` : ''}
        <div
          id="free-samples-panel"
          aria-labelledby="free-samples-header"
          class="overflow-hidden transition-all duration-300 ease-in-out mini-cart__slide-up-panel ${isOpen ? 'max-h-96 opacity-100 z-10' : 'max-h-0 opacity-0 -z-10'}"
        >
          <section class="mini-cart__free-samples-container relative transition-all duration-300 ease-in-out pb-4 ${isOpen ? 'opacity-100' : 'opacity-0'}">
            <div>
              ${!canAddMore && this.freeSamplesConfig.reachedLimitErrorMessage && this.addedSamples.length > 0 ? `
                <p class="text-xs">${this.freeSamplesConfig.reachedLimitErrorMessage}</p>
              ` : !eligibleTier ? `
                <p class="text-xs">${this.freeSamplesConfig.description || ''}</p>
              ` : `
                <div class="mini-cart__free-samples-message">${sampleStatus.message}</div>
              `}
            </div>

            ${eligibleTier && canAddMore ? `
              <div class="mini-cart__free-samples-inner relative overflow-x-auto whitespace-nowrap">
                ${eligibleTierProducts.map(product => `
                  <div class="mini-cart__free-samples-item border inline-flex items-center gap-3 p-4 w-2/5">
                    <div class="mini-cart__free-samples-fig">
                      <img src="${product.featured_image}" alt="${product.title}" class="h-10 w-10 border" width="40" height="40" loading="lazy"/>
                    </div>
                    <div class="mini-cart__free-samples-meta text-[0.875rem] flex-1 flex flex-col cart-drawer-heading justify-between text-center">
                      <h2 class="mini-cart__meta-title matter-regular text-[0.875rem] leading-[1.08] pb-2 text-wrap" title="${product.title}">${product.title}</h2>
                      <button
                        type="button"
                        class="mini-cart__free-samples-add-btn mini-cart__button js-add-free-sample ${this.isLoading(product.id) ? 'cursor-not-allowed' : ''}"
                        ${this.isLoading(product.id) || this.isAnyLoading() ? 'disabled' : ''}
                        data-product-id="${product.id}"
                      >
                        ${this.isLoading(product.id) ? (this.settings.freeSampleAddingText || 'Adding...') : (this.settings.freeSampleAddButton || 'Add')}
                      </button>
                    </div>
                  </div>
                `).join('')}
              </div>
            ` : ''}
          </section>
        </div>
      </div>
    `;
  }

  renderGWP() {
    if (!this.gwpConfig.active || !this.data?.item_count) return '';

    const isOpen = this.isAccordionOpen('gwp');
    const isEligible = this.isEligibleForGWP();
    const hasSelected = this.hasAnyGWPSelected();
    const hasReachedMax = this.hasReachedGWPMax();
    const availableProducts = this.getAvailableGWPProducts();

    return `
      <div class="mini-cart__gwp accordion-item body-3 cart-drawer-heading border-b">
        <h3>
          <button
            aria-expanded="${isOpen}"
            aria-controls="gwp-panel"
            id="gwp-header"
            class="flex items-center justify-between cart-drawer-heading w-full text-left py-4 cursor-pointer js-accordion-toggle"
            data-accordion="gwp"
            ${!isEligible ? 'disabled' : ''}
          >
            <div class="flex items-center gap-2 justify-between w-[calc(100%-1rem)] text-left">
              <span>
                <span class="flex gap-2 relative items-center">
                  ${this.settings.showAccordionIcons ? `<span class="mini-cart__util-icon">${this.renderGiftIcon()}</span>` : ''}
                  ${this.gwpConfig.heading || 'Free Gift With Purchase'}
                </span>
                ${hasSelected ? '<span class="text-xs">(Gift Added)</span>' : ''}
              </span>
              ${!isEligible ? `
                <span class="text-xs font-normal text-gray-500">${this.getGWPAmountAwayMessage()}</span>
              ` : !hasSelected ? `
                <span class="mini-cart__truncated-preview-text">${this.gwpConfig.eligibleText || 'Choose your free gift!'}</span>
              ` : ''}
            </div>
            ${isEligible ? this.renderChevronIcon(isOpen) : ''}
          </button>
        </h3>
        ${isOpen ? `
          <button class="w-full h-full fixed inset-0 z-5 js-accordion-overlay" data-accordion="gwp" style="background-color: var(--global-mask-color); backdrop-filter: var(--global-mask-blur);">
            <span class="sr-only">close</span>
          </button>
        ` : ''}
        <div
          id="gwp-panel"
          aria-labelledby="gwp-header"
          class="overflow-hidden transition-all duration-300 ease-in-out mini-cart__slide-up-panel ${isOpen ? 'max-h-[600px] opacity-100 z-10' : 'max-h-0 opacity-0 -z-10'}"
        >
          <section class="mini-cart__gwp-container relative transition-all duration-300 ease-in-out pb-4 ${isOpen ? 'opacity-100' : 'opacity-0'}">
            ${hasSelected && this.gwpConfig.selectedMessage ? `
              <p class="mb-3 text-green-600 font-medium">${this.gwpConfig.selectedMessage}</p>
            ` : isEligible ? `
              <p class="mb-3">${this.gwpConfig.description || 'Choose one free gift with your purchase'}</p>
            ` : ''}

            ${isEligible && availableProducts.length ? `
              <div class="mini-cart__gwp-inner relative grid grid-cols-12 gap-4">
                ${availableProducts.map(product => {
                  const isAdded = this.addedGWP.includes(product.id);
                  return `
                    <div class="mini-cart__gwp-item border inline-flex items-center gap-3 p-4 ${isAdded ? 'border-green-500 bg-green-50' : ''} ${hasReachedMax && !isAdded ? 'opacity-50' : ''} ${availableProducts.length > 1 ? 'col-span-6' : 'col-span-12'}">
                      <div class="mini-cart__gwp-fig w-20 h-20">
                        <img
                          srcset="${getCroppedImageUrl(product.featured_image, [120, 160, 240, 320])}"
                          src="${getCroppedImageUrl(product.featured_image, 160)}"
                          alt="${product.title}"
                          class="w-full h-full object-cover"
                          width="80"
                          height="80"
                          loading="lazy"
                        />
                      </div>
                      <div class="mini-cart__gwp-meta text-xs flex-1 flex flex-col justify-between text-center gap-2">
                        <h2 class="mini-cart__meta-title matter-regular text-xs text-wrap" title="${product.title}">${product.title}</h2>
                        <span class="mini-cart__meta-price text-xs line-through text-gray-500">${this.settings.currencySymbol}${(product.price / 100).toFixed(2)}</span>
                        ${!isAdded ? `
                          <button
                            type="button"
                            class="mini-cart__gwp-add-btn mini-cart__button js-add-gwp ${this.isLoading(product.id) || hasReachedMax ? 'cursor-not-allowed' : ''}"
                            ${this.isLoading(product.id) || this.isAnyLoading() || hasReachedMax ? 'disabled' : ''}
                            data-product-id="${product.id}"
                          >
                            ${this.isLoading(product.id) ? (this.gwpConfig.addingText || 'Adding...') : (this.gwpConfig.addButton || 'Add Gift')}
                          </button>
                        ` : `
                          <p class="text-xs mb-4 text-center text-green-600">✓ Gift Added</p>
                        `}
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>
            ` : ''}
          </section>
        </div>
      </div>
    `;
  }

  renderOrderNote() {
    if (!this.settings.orderNoteEnabled) return '';

    const isOpen = this.isAccordionOpen('order-note');

    return `
      <div class="accordion-item body-3 cart-drawer-heading border-color border-b">
        <h3>
          <button
            aria-expanded="${isOpen}"
            aria-controls="order-note-panel"
            id="order-note-header"
            class="order-note-panel flex items-center cart-drawer-heading justify-between w-full py-4 cursor-pointer js-accordion-toggle"
            data-accordion="order-note"
          >
            <div class="flex items-center gap-2 justify-between w-[calc(100%-1rem)] text-left">
              <span class="flex gap-2 relative items-center">
                ${this.settings.showAccordionIcons ? `<span class="mini-cart__util-icon">${this.renderNoteIcon()}</span>` : ''}
                ${this.settings.orderNoteHeading || 'Add a handwritten note'}
              </span>
              ${this.orderNote && !isOpen && !this.hasUnsavedChanges ? `
                <span class="mini-cart__truncated-preview-text">${this.orderNote}</span>
              ` : ''}
            </div>
            ${this.renderChevronIcon(isOpen)}
          </button>
        </h3>
        ${isOpen ? `
          <button class="w-full h-full fixed inset-0 z-5 js-accordion-overlay" data-accordion="order-note" style="background-color: var(--global-mask-color); backdrop-filter: var(--global-mask-blur);">
            <span class="sr-only">close</span>
          </button>
        ` : ''}
        <div
          id="order-note-panel"
          aria-labelledby="order-note-header"
          class="overflow-hidden transition-all duration-300 ease-in-out mini-cart__slide-up-panel ${isOpen ? 'max-h-96 opacity-100 z-10' : 'max-h-0 opacity-0 -z-10'}"
        >
          <div class="flex items-center justify-between">
           <span class="body-3">Shipping Note</span>
            <button class="w-12 h-12 right-0 flex items-center justify-center js-accordion-close" data-accordion="order-note">
              ${this.renderCloseIcon()}
              <span class="sr-only">Close</span>
            </button>
          </div>
          <form class="text-area-wrap relative transition-all duration-300 ease-in-out pb-4 ${isOpen ? 'opacity-100' : 'opacity-0'}" id="order-note-form">
            <fieldset>
              <div class="relative">
                <textarea
                  ${this.isOrderNoteUpdating ? 'disabled' : ''}
                  class="block w-full border p-2 resize-none overflow-auto js-order-note-input"
                  name="note"
                  id="cart-note"
                  maxlength="280"
                  rows="4"
                  placeholder="${this.settings.orderNotePlaceholder || 'Add a note to your order (optional)'}"
                >${this.orderNote}</textarea>
                <div class="absolute bottom-0 right-0 text-xs p-2">
                  <span id="current_count">${this.orderNote.length}</span>
                  <span>/280</span>
                </div>
              </div>
              <div class="flex justify-end items-start gap-4 mt-4">
                <span class="flex-1 text-xs hidden" id="special-character-error">${this.settings.orderNoteErrorText || '*Special characters not allowed.'}</span>
                ${this.orderNote.length ? `
                  <button type="button" class="mini-cart__button js-clear-order-note">${this.settings.orderNoteClearButton || 'Clear'}</button>
                ` : ''}
                <button type="submit" class="mini-cart__button ${this.hasUnsavedChanges ? 'bg-blue-600 text-white' : ''} js-save-order-note">
                  ${this.hasUnsavedChanges ? (this.settings.orderNoteSaveUnsavedButton || 'Save*') : (this.settings.orderNoteSaveButton || 'Save')}
                </button>
              </div>
            </fieldset>
          </form>
        </div>
      </div>
    `;
  }

  renderDiscountCode() {
    if (!this.settings.discountCodeEnabled) return '';

    const isOpen = this.isAccordionOpen('discount-code');

    return `
      <div class="accordion-item border-color border-b">
        <h3>
          <button
            aria-expanded="${isOpen}"
            aria-controls="discount-code-panel"
            id="discount-code-header"
            class="flex items-center  ${this.settings.cartheading} cart-drawer-heading  justify-between w-full py-4 cursor-pointer js-accordion-toggle"
            data-accordion="discount-code"
          >
            <div class="flex items-center gap-2 justify-between w-[calc(100%-1rem)] text-left">
              <span class="flex gap-2 relative items-center">
                ${this.settings.showAccordionIcons ? `<span class="mini-cart__util-icon">${this.renderDiscountIcon()}</span>` : ''}
                ${this.settings.discountCodeLabel || 'Add discount code'}
              </span>
              ${this.appliedCodes.length && !isOpen ? `
                <span class=" body-5 mini-cart__truncated-preview-text">${this.getDiscountAppliedLabel()}</span>
              ` : ''}
            </div>
            ${this.renderChevronIcon(isOpen)}
          </button>
        </h3>
        ${isOpen ? `
          <button class="w-full h-full fixed inset-0 z-5 js-accordion-overlay" data-accordion="discount-code" style="background-color: var(--global-mask-color); backdrop-filter: var(--global-mask-blur);">
            <span class="sr-only">close</span>
          </button>
        ` : ''}
        <div
          id="discount-code-panel"
          aria-labelledby="discount-code-header"
          class="overflow-hidden transition-all duration-300 ease-in-out mini-cart__slide-up-panel ${isOpen ? 'max-h-96 opacity-100 z-10' : 'max-h-0 opacity-0 -z-10'}"
        >
          <div class="flex justify-between items-center mb-4">
            <h3 id="mini-cart__discount-code-form-label" class="cart-drawer-heading  ${this.settings.cartheading}">${this.settings.discountCodeLabel || 'Add discount code'}</h3>
            <button class="w-6 h-6 right-0 flex items-center justify-center js-accordion-close cursor-pointer" data-accordion="discount-code">
              ${this.renderCloseIcon()}
              <span class="sr-only">Close</span>
            </button>
          </div>
          <form class="discount-code-form relative transition-all duration-300 ease-in-out pb-4 ${isOpen ? 'opacity-100' : 'opacity-0'}" id="discount-code-form" aria-labelledby="mini-cart__discount-code-form-label">
            <fieldset>
              <div class="flex gap-2 items-center">
                <label for="mini-cart__discount-code-input" class="sr-only">${this.settings.discountCodePlaceholder || 'Enter discount code'}</label>
                <input
                  id="mini-cart__discount-code-input"
                  type="text"
                  class="flex-1 js-discount-code-input body-4"
                  placeholder="${this.settings.discountCodePlaceholder || 'Enter code'}"
                  name="discount"
                  value="${this.discountCodeInput.trim()}"
                >
                <button type="submit" class="mini-cart__button discount-button ${this.settings.discount_button_text}" ${!this.discountCodeInput.trim() || this.isApplyingDiscount ? 'disabled' : ''}>
                  ${this.isApplyingDiscount ? (this.settings.discountCodeApplyingText || 'Applying...') : (this.settings.discountCodeApplyButton || 'Apply')}
                </button>
              </div>

              ${this.appliedCodes.length ? `
                <div class="mt-4">
                  <p class="text-xs mb-4 cart-drawer-heading ${this.settings.cartheading}">${this.settings.appliedDiscountsHeading || 'Applied Discounts:'}</p>
                  <div class="space-y-2">
                    ${this.appliedCodes.map(code => `
                      <div class="flex items-center justify-between bg-green-50 border border-green-200 rounded p-2">
                        <span class="text-xs body-5 text-green-800">${code}</span>
                        <button type="button" class="body-5 text-green-600 hover:text-green-800 text-xs underline js-remove-discount cursor-pointer" data-code="${code}">
                          ${this.settings.removeDiscount || 'Remove'}
                        </button>
                      </div>
                    `).join('')}
                  </div>
                </div>
              ` : ''}

              ${this.discountErrorMessage ? `
                <div class="mt-2 text-red-600 text-xs">${this.discountErrorMessage}</div>
              ` : ''}
            </fieldset>
          </form>
        </div>
      </div>
    `;
  }

  renderShippingRates() {
    if (!this.settings.showShippingRatesEnabled) return '';

    const isOpen = this.isAccordionOpen('shipping-rates');
    const shippingRange = this.getShippingRange();
    const availableCountries = this.settings.availableCountries || [
      { name: 'United States', iso_code: 'US' },
      { name: 'Canada', iso_code: 'CA' },
      { name: 'United Kingdom', iso_code: 'GB' },
      { name: 'Australia', iso_code: 'AU' }
    ];

    return `
      <div class="accordion-item body-3 cart-drawer-heading  border-color border-b">
        <h3>
          <button
            aria-expanded="${isOpen}"
            aria-controls="shipping-rates-panel"
            id="shipping-rates-header"
            class="flex items-center justify-between w-full py-4 transition-all duration-150 cursor-pointer js-accordion-toggle"
            data-accordion="shipping-rates"
          >
            <div class="flex items-center gap-2 justify-between w-[calc(100%-1rem)] text-left">
              <span class="flex gap-2 relative items-center">
                ${this.settings.showAccordionIcons ? `<span class="mini-cart__util-icon">${this.renderShippingIcon()}</span>` : ''}
                ${this.settings.shippingRatesHeading || 'Shipping rates'}
              </span>
              ${shippingRange ? `
                <div class="mini-cart__truncated-preview-text">
                  <span>
                    ${formatMoney(shippingRange.min)}
                    ${shippingRange.isRange ? `
                      – ${formatMoney(shippingRange.max)}
                      <span>${(this.settings.shippingOptionsLabelTemplate || '({count} options)').replace(/{count}/g, shippingRange.count)}</span>
                    ` : ''}
                  </span>
                </div>
              ` : ''}
            </div>
            ${this.renderChevronIcon(isOpen)}
          </button>
        </h3>
        ${isOpen ? `
          <button class="w-full h-full fixed inset-0 z-5 js-accordion-overlay" data-accordion="shipping-rates" style="background-color: var(--global-mask-color); backdrop-filter: var(--global-mask-blur);">
            <span class="sr-only">close</span>
          </button>
        ` : ''}
        <div
          id="shipping-rates-panel"
          aria-labelledby="shipping-rates-header"
          class="mini-cart__slide-up-panel overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[80vh] opacity-100 z-10' : 'max-h-0 opacity-0 -z-10'}"
        >
          <div class="max-h-[80vh] overflow-y-auto">
            <div class="flex justify-end">
              <button class="w-12 h-12 right-0 flex items-center justify-center transition-all duration-150 cursor-pointer js-accordion-close" data-accordion="shipping-rates">
                ${this.renderCloseIcon()}
                <span class="sr-only">close</span>
              </button>
            </div>
            <div class="relative transition-all duration-300 ease-in-out ${isOpen ? 'opacity-100' : 'opacity-0'}">
              <div class="mb-4 space-y-3">
                <div>
                  <label for="shipping-country" class="block text-xs font-medium mb-1">${this.settings.countryLabel || 'Country'}</label>
                  <select id="shipping-country" class="w-full border border-gray-300 rounded px-3 py-2 text-sm js-shipping-country">
                    ${availableCountries.sort((a, b) => a.name.localeCompare(b.name)).map(country => `
                      <option value="${country.iso_code}" ${country.iso_code === this.formCountry ? 'selected' : ''}>${country.name}</option>
                    `).join('')}
                  </select>
                </div>

                <div>
                  <label for="shipping-province" class="block text-xs font-medium mb-1">${this.settings.provinceLabel || 'State/Province'} ${this.availableStates.length ? '' : '(Optional)'}</label>
                  ${this.availableStates.length ? `
                    <select id="shipping-province" class="w-full border border-gray-300 rounded px-3 py-2 text-sm js-shipping-province">
                      <option value="">Select ${this.settings.provinceLabel || 'State/Province'}</option>
                      ${this.availableStates.map(state => `
                        <option value="${state.code}" ${state.code === this.formProvince ? 'selected' : ''}>${state.name}</option>
                      `).join('')}
                    </select>
                  ` : `
                    <input id="shipping-province" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm js-shipping-province" placeholder="e.g., Region" value="${this.formProvince}"/>
                  `}
                </div>

                <div>
                  <label for="shipping-zip" class="block text-xs font-medium mb-1">${this.settings.zipLabel || 'ZIP/Postal Code'}</label>
                  <input id="shipping-zip" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm js-shipping-zip" placeholder="e.g., 90001" value="${this.formZip}"/>
                </div>
              </div>

              ${this.shippingRates.length ? `
                <ul class="mb-4">
                  ${this.shippingRates.map(rate => `
                    <li class="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0 text-xs">
                      <span>${rate?.name}</span>
                      <span class="font-medium">${formatMoney(rate.price)}</span>
                    </li>
                  `).join('')}
                </ul>
              ` : ''}

              <button class="mini-cart__button button w-full mb-4 js-fetch-shipping-rates">
                ${this.shippingRates.length ? (this.settings.shippingRatesUpdateButton || 'Update shipping estimate') : (this.settings.shippingRatesEstimateButton || 'Estimate shipping rates')}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderTotals() {
    if (!this.data?.item_count) return '';

    return `
      <div class="mini-cart__totals">
        <ul class="mini-cart__totals-list pt-4">
        ${this.data?.total_price !== this.data?.original_total_price ? `
          <li class="mini-cart__totals-item flex justify-between w-full pb-1">
          <span class="cart-drawer-heading font-bold ${this.settings.cartheading}">Subtotal</span>
          <span class="cart-drawer-heading opacity-50 ${this.settings.cartheading}">${formatMoney(this.data?.original_total_price)}</span>
          </li>
          ` : ''}
          ${this.data?.total_discount > 0 ? `
            <li class="mini-cart__totals-item flex justify-between w-full pb-1">
            <span class="cart-drawer-heading font-bold ${this.settings.cartheading}">Discount</span>
            <span class="cart-drawer-heading ${this.settings.cartheading}">-${formatMoney(this.data?.total_discount)}</span>
            </li>
            ` : ''}
            <li class="mini-cart__totals-item  flex justify-between w-full">
              <span class="cart-drawer-heading font-bold  ${this.settings.cartheading} ">${this.settings.totalText || 'Subtotal'}</span>
              ${!this.settings.hidePriceInCheckout ? `<span class="cart-drawer-heading totals-price font-bold ${this.settings.cartheading}">${formatMoney(this.data?.total_price)}</span>` : ''}
            </li>
        </ul>
        </div>
        <div class="checkout-line body-6">${this.settings.checkout_line_text || ''}</div>
    `;
  }

  attachEventListeners() {
    // Close button
    this.querySelector('.js-mini-cart-close')?.addEventListener('click', () => this.closeMiniCart());

    // Empty button (when cart is empty)
    this.querySelector('.js-close-mini-cart')?.addEventListener('click', () => this.closeMiniCart());

    // Empty cart button
    this.querySelector('.js-empty-cart')?.addEventListener('click', () => this.emptyMiniCart());

    // Checkout link
    this.querySelector('.js-checkout-link')?.addEventListener('click', (e) => this.handleCheckout(e));

    // Quantity buttons
    this.querySelectorAll('.js-qty-decrease, .js-qty-increase').forEach(btn => {
      btn.addEventListener('click', () => {
        const itemKey = btn.dataset.itemKey;
        const itemId = btn.dataset.itemId;
        const newQty = parseInt(btn.dataset.newQty, 10);
        if (newQty >= 0) {
          this.updateItemQuantity(itemKey, newQty, itemId);
        }
      });
    });

    // Remove item buttons
    this.querySelectorAll('.js-remove-item').forEach(btn => {
      btn.addEventListener('click', () => {
        const itemKey = btn.dataset.itemKey;
        const itemId = btn.dataset.itemId;
        this.updateItemQuantity(itemKey, 0, itemId);
      });
    });

    // Accordion toggles
    this.querySelectorAll('.js-accordion-toggle').forEach(btn => {
      btn.addEventListener('click', () => {
        const accordionId = btn.dataset.accordion;
        this.toggleAccordion(accordionId);
      });
    });

    // Accordion overlays
    this.querySelectorAll('.js-accordion-overlay').forEach(btn => {
      btn.addEventListener('click', () => {
        const accordionId = btn.dataset.accordion;
        this.closeAccordion(accordionId);
      });
    });

    // Accordion close buttons
    this.querySelectorAll('.js-accordion-close').forEach(btn => {
      btn.addEventListener('click', () => {
        const accordionId = btn.dataset.accordion;
        this.closeAccordion(accordionId);
      });
    });

    // Upsell forms
    this.querySelectorAll('.js-upsell-form').forEach(form => {
      form.addEventListener('submit', (e) => {
        const productId = parseInt(form.dataset.productId, 10);
        const product = this.displayedUpsellProducts.find(p => p.id === productId);
        if (product) {
          this.addUpsellProductToCart(e, product);
        }
      });

      // Variant select change
      const variantSelect = form.querySelector('select[name="id"]');
      if (variantSelect) {
        variantSelect.addEventListener('change', (e) => {
          const productId = parseInt(form.dataset.productId, 10);
          this.selectedVariantIds[productId] = parseInt(e.target.value, 10);
        });
      }
    });

    // Show more upsell button
    this.querySelector('.js-show-more-upsell')?.addEventListener('click', () => this.showMoreUpsell());

    // Free sample buttons
    this.querySelectorAll('.js-add-free-sample').forEach(btn => {
      btn.addEventListener('click', () => {
        const productId = parseInt(btn.dataset.productId, 10);
        const eligibleTier = this.getEligibleTier();
        const product = eligibleTier?.products.find(p => p.id === productId);
        if (product) {
          this.addFreeSampleToCart(product);
        }
      });
    });

    // GWP buttons
    this.querySelectorAll('.js-add-gwp').forEach(btn => {
      btn.addEventListener('click', () => {
        const productId = parseInt(btn.dataset.productId, 10);
        const product = this.gwpConfig.products.find(p => p.id === productId);
        if (product) {
          this.addGWPToCart(product);
        }
      });
    });

    // Order note form
    const orderNoteForm = this.querySelector('#order-note-form');
    if (orderNoteForm) {
      orderNoteForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const textarea = this.querySelector('.js-order-note-input');
        if (textarea) {
          this.saveOrderNote(textarea.value).then(() => {
            this.closeAccordion('order-note');
          });
        }
      });

      const textarea = this.querySelector('.js-order-note-input');
      if (textarea) {
        textarea.addEventListener('input', (e) => {
          this.orderNote = e.target.value;
          this.hasUnsavedChanges = true;
          const counter = this.querySelector('#current_count');
          if (counter) counter.textContent = e.target.value.length;
        });

        textarea.addEventListener('keydown', (e) => {
          const allowedCharacters = /^[A-Za-z0-9~@#$^*()_+\[\]{}|\\,.?!: -]+$/;
          if (!allowedCharacters.test(e.key) && e.key.length === 1) {
            e.preventDefault();
            const errorEl = this.querySelector('#special-character-error');
            if (errorEl) errorEl.classList.remove('hidden');
          } else {
            const errorEl = this.querySelector('#special-character-error');
            if (errorEl) errorEl.classList.add('hidden');
          }
        });
      }
    }

    this.querySelector('.js-clear-order-note')?.addEventListener('click', () => this.clearOrderNote());

    // Discount code form
    const discountForm = this.querySelector('#discount-code-form');
    if (discountForm) {
      discountForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const input = this.querySelector('.js-discount-code-input');
        if (input) {
          this.applyDiscountCode(input.value);
        }
      });

      const input = this.querySelector('.js-discount-code-input');
      const applyButton = discountForm.querySelector('.mini-cart__button.discount-button');
      if (input) {
        input.addEventListener('input', (e) => {
          this.discountCodeInput = e.target.value;
          if (applyButton) {
            applyButton.disabled = !this.discountCodeInput.trim() || this.isApplyingDiscount;
          }
        });
      }
    }

    // Remove discount buttons
    this.querySelectorAll('.js-remove-discount').forEach(btn => {
      btn.addEventListener('click', () => {
        const code = btn.dataset.code;
        this.removeDiscountCode(code);
      });
    });

    // Shipping rates
    const countrySelect = this.querySelector('.js-shipping-country');
    if (countrySelect) {
      countrySelect.addEventListener('change', async (e) => {
        this.formCountry = e.target.value;
        this.formProvince = '';
        // For simplicity, we're not loading states dynamically in this vanilla implementation
        // but you can add that functionality if needed
        this.render();
      });
    }

    const provinceInput = this.querySelector('.js-shipping-province');
    if (provinceInput) {
      provinceInput.addEventListener('change', (e) => {
        this.formProvince = e.target.value;
      });
      provinceInput.addEventListener('input', (e) => {
        this.formProvince = e.target.value;
      });
    }

    const zipInput = this.querySelector('.js-shipping-zip');
    if (zipInput) {
      zipInput.addEventListener('input', (e) => {
        this.formZip = e.target.value;
      });
    }

    this.querySelector('.js-fetch-shipping-rates')?.addEventListener('click', () => {
      this.fetchShippingRates({
        country: this.formCountry,
        province: this.formProvince,
        zip: this.formZip
      });
    });

    // Initialize form values on first render
    if (!this.formCountry && this.settings.availableCountries?.length) {
      this.formCountry = this.settings.availableCountries[0].iso_code;
    }

    // Load saved shipping data
    this.loadShippingFromLocalStorage();

    // Sync order note from cart data
    if (this.data?.note && !this.orderNote) {
      this.orderNote = this.data.note;
    }
  }
}

export default MiniCart;

// Auto-register the component
if (!customElements.get('mini-cart-component')) {
  customElements.define('mini-cart-component', MiniCart);
}
